import { useEffect, useRef } from 'react';
import { useProxyStore } from '../store/proxy-store';
import { useElectronProxy } from './useElectronProxy';

// Performance-optimized comparison functions to avoid JSON.stringify blocking
function shallowCompareArrays<T>(a: T[] = [], b: T[] = []): boolean {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return true;
  }

  return false;
}

function shallowCompareObjects<T extends Record<string, unknown>>(
  a: T = {} as T,
  b: T = {} as T,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return true;

  for (const key of aKeys) {
    if (a[key] !== b[key]) return true;
  }

  return false;
}

/**
 * Hook that automatically updates the proxy configuration when settings change
 * This ensures that match/replace rules and other settings are applied in real-time
 */
export function useProxyAutoUpdate() {
  const settings = useProxyStore((state) => state.settings);
  const isProxyRunning = useProxyStore((state) => state.isProxyRunning);
  const interceptRulesHydrationPending = useProxyStore(
    (state) => state.interceptRulesHydrationPending,
  );
  const { updateProxyConfig } = useElectronProxy();
  const previousSettingsRef = useRef(settings);
  const previousHydrationPendingRef = useRef(interceptRulesHydrationPending);
  const wasProxyRunningRef = useRef(isProxyRunning);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const proxyJustStarted = isProxyRunning && !wasProxyRunningRef.current;

    wasProxyRunningRef.current = isProxyRunning;

    if (!isProxyRunning) {
      previousSettingsRef.current = settings;

      return;
    }

    const prevSettings = previousSettingsRef.current;
    const settingsChanged =
      proxyJustStarted ||
      previousHydrationPendingRef.current !== interceptRulesHydrationPending ||
      prevSettings.interceptEnabled !== settings.interceptEnabled ||
      prevSettings.interceptResponsesEnabled !==
        settings.interceptResponsesEnabled ||
      prevSettings.showOnlyInScopeRequests !==
        settings.showOnlyInScopeRequests ||
      shallowCompareArrays(
        prevSettings.interceptRules,
        settings.interceptRules,
      ) ||
      prevSettings.matchReplaceEnabled !== settings.matchReplaceEnabled ||
      prevSettings.modifyHeaders !== settings.modifyHeaders ||
      shallowCompareArrays(
        prevSettings.matchReplaceRules,
        settings.matchReplaceRules,
      ) ||
      shallowCompareObjects(
        prevSettings.customHeaders,
        settings.customHeaders,
      ) ||
      shallowCompareArrays(
        prevSettings.removeHeaders,
        settings.removeHeaders,
      ) ||
      shallowCompareArrays(prevSettings.allowedHosts, settings.allowedHosts) ||
      shallowCompareArrays(prevSettings.blockedHosts, settings.blockedHosts) ||
      prevSettings.upstreamEnabled !== settings.upstreamEnabled ||
      prevSettings.upstreamHost !== settings.upstreamHost ||
      prevSettings.upstreamPort !== settings.upstreamPort ||
      prevSettings.validateUpstreamTls !== settings.validateUpstreamTls ||
      shallowCompareObjects(
        prevSettings.upstreamAuth ?? {},
        settings.upstreamAuth ?? {},
      );

    if (settingsChanged) {
      // Debounce updates to avoid rapid-fire changes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        updateProxyConfig(settings);
        previousSettingsRef.current = settings;
        previousHydrationPendingRef.current = interceptRulesHydrationPending;
      }, 500); // 500ms debounce
    } else {
      previousSettingsRef.current = settings;
      previousHydrationPendingRef.current = interceptRulesHydrationPending;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    settings,
    isProxyRunning,
    interceptRulesHydrationPending,
    updateProxyConfig,
  ]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
}
